import { useEffect, useMemo, useRef, useState } from "react";
import "./GuideApprovalReview.css";
import type { AdminGuideResponse, GuideReviewSummary } from "@rnavoyager/contracts";
import {
  approveAdminGuide,
  getAdminGuideEvidence,
  getAdminGuideReviews,
  retryAdminGuideReview,
} from "./api";
import { bindGuideReviewEvidence } from "./guide-review-evidence";

const confirmations = [
  "我已审阅目标、实际计划、工具调用与关键参数",
  "我已审阅实际输出、失败记录、科学判据与预算",
  "我已审阅数据披露、计算预测声明与适用局限",
] as const;

export function GuideApprovalReview({
  guide,
  onClose,
  onApproved,
}: {
  guide: AdminGuideResponse;
  onClose: () => void;
  onApproved: () => Promise<void>;
}) {
  const [files, setFiles] = useState<{ trace?: unknown; report?: unknown }>({});
  const [checks, setChecks] = useState([false, false, false]);
  const [reviews, setReviews] = useState<GuideReviewSummary[]>([]);
  const [reviewError, setReviewError] = useState("");
  const [decisions, setDecisions] = useState<
    Record<string, { disposition: "acknowledged" | "disagree" | ""; reason: string }>
  >({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  useEffect(() => {
    let active = true;
    setFiles({});
    setChecks([false, false, false]);
    setError("");
    if (guide.canary) {
      void getAdminGuideEvidence(guide.definition.definitionId).then(
        (documents) => {
          if (active) setFiles(documents);
        },
        (cause: unknown) => {
          if (active) setError(cause instanceof Error ? cause.message : "读取验收证据失败");
        },
      );
    }
    return () => {
      active = false;
    };
  }, [guide.definition.definitionId, guide.canary?.traceDigest]);
  useEffect(() => {
    let active = true;
    let requestNumber = 0;
    const load = async () => {
      const current = ++requestNumber;
      try {
        const value = await getAdminGuideReviews(guide.definition.definitionId);
        if (active && current === requestNumber) {
          setReviews(value);
          setReviewError("");
        }
      } catch (cause) {
        if (active && current === requestNumber) {
          setReviews([]);
          setReviewError(cause instanceof Error ? cause.message : "读取 AI 审阅失败");
        }
      }
    };
    setReviews([]);
    setDecisions({});
    setReviewError("");
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [guide.definition.definitionId, guide.definition.contentDigest, guide.canary?.traceDigest]);
  const bound = useMemo(() => {
    if (files.trace === undefined || files.report === undefined) return {};
    try {
      return { evidence: bindGuideReviewEvidence(guide, files.trace, files.report) };
    } catch (cause) {
      return { problem: cause instanceof Error ? cause.message : "验收证据无法核对" };
    }
  }, [guide, files]);
  const evidence = bound.evidence;
  const review = reviews.find(
    (item) =>
      item.contentDigest === guide.definition.contentDigest &&
      (guide.canary
        ? item.stage === "canary" &&
          item.traceDigest === guide.canary.traceDigest &&
          item.validatorDigest === guide.canary.validatorDigest
        : item.stage === "definition"),
  );
  useEffect(() => {
    setDecisions({});
    setChecks([false, false, false]);
  }, [review?.reviewId, review?.resultDigest]);
  const reviewComplete =
    review?.status === "completed" &&
    review.resultDigest !== null &&
    review.totalChunks !== null &&
    review.coveredChunks === review.totalChunks;
  const decisionsComplete =
    reviewComplete &&
    review.findings.every((finding) => {
      const decision = decisions[finding.findingId];
      return (
        decision &&
        decision.disposition !== "" &&
        (decision.disposition !== "disagree" || decision.reason.trim().length > 0)
      );
    });
  const canApprove =
    guide.effectiveStatus === "validated" &&
    guide.canaryCurrent &&
    evidence?.report.passed === true &&
    reviewComplete &&
    decisionsComplete;

  async function submit() {
    if (
      submitting.current ||
      !canApprove ||
      !evidence ||
      !review?.resultDigest ||
      !checks.every(Boolean)
    )
      return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await approveAdminGuide(guide.definition.definitionId, {
        contentDigest: guide.definition.contentDigest,
        traceDigest: evidence.report.traceDigest,
        reviewId: review.reviewId,
        reviewDigest: review.resultDigest,
        findingDecisions: review.findings.map((finding) => ({
          findingId: finding.findingId,
          disposition: decisions[finding.findingId]!.disposition as "acknowledged" | "disagree",
          reason: decisions[finding.findingId]!.reason,
        })),
        reviewedGoalPlanAndTools: true,
        reviewedOutputsFailuresAndBudget: true,
        reviewedDisclosureAndLimitations: true,
      });
      await onApproved();
    } catch (cause) {
      setChecks([false, false, false]);
      setError(cause instanceof Error ? cause.message : "批准失败，请重新核对当前证据");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  async function retryReview() {
    if (!review || busy || !["failed", "cancelled", "stale"].includes(review.status)) return;
    setBusy(true);
    setReviewError("");
    try {
      await retryAdminGuideReview(guide.definition.definitionId, review.reviewId);
      setReviews(await getAdminGuideReviews(guide.definition.definitionId));
    } catch (cause) {
      setReviewError(cause instanceof Error ? cause.message : "重试 AI 审阅失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-review-title"
        className="modal-card guide-review-dialog"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) onClose();
        }}
      >
        <header className="modal-header">
          <h2 id="guide-review-title">审阅 Guide 验收证据</h2>
          <button ref={closeButton} type="button" disabled={busy} onClick={onClose}>
            关闭
          </button>
        </header>
        <h3>{guide.definition.displayName}</h3>
        <p>{guide.definition.description}</p>
        <p>验收运行器登记的完整证据会自动加载并核对，管理员无需上传文件。</p>
        <h3>Guide 具体内容与建议步骤</h3>
        <p>{guide.definition.applicability.intentSummary}</p>
        <ol>
          {guide.definition.guidance.suggestedSteps.map((step) => (
            <li key={step.stepId}>
              <strong>{step.title}</strong>：{step.purpose}
              {step.suggestedTools.length > 0 && (
                <pre className="guide-review-output">
                  {JSON.stringify(step.suggestedTools, null, 2)}
                </pre>
              )}
              {step.alternatives.length > 0 && <p>替代方案：{step.alternatives.join("；")}</p>}
              {step.evidenceToCapture.length > 0 && (
                <p>应保留的证据：{step.evidenceToCapture.join("；")}</p>
              )}
            </li>
          ))}
        </ol>
        <details>
          <summary>查看完整 Guide 定义</summary>
          <pre className="guide-review-output">{JSON.stringify(guide.definition, null, 2)}</pre>
        </details>
        <h3>独立 AI 审阅建议</h3>
        {reviewError && <p role="alert">{reviewError}</p>}
        {!review && !reviewError && <p role="status">当前版本尚无 AI 审阅任务。</p>}
        {review && (
          <section aria-label="AI 审阅结果">
            <p role="status">
              状态：
              {review.failureCode === "runtime_capacity_exceeded" &&
              ["queued", "running"].includes(review.status)
                ? "等待运行资源"
                : reviewStatusLabel(review.status)}
              ；模型：{review.modelDisplayName}； 独立上下文；与验收模型是否不同：未知。
            </p>
            <p>
              覆盖分段：{review.coveredChunks} / {review.totalChunks ?? "未知"}。
              {review.failureCode &&
              !(
                review.failureCode === "runtime_capacity_exceeded" &&
                ["queued", "running"].includes(review.status)
              )
                ? ` 失败原因：${review.failureCode}。`
                : ""}
            </p>
            {review.stage === "definition" && <p>此建议仅审阅定义，尚未审阅真实验收证据。</p>}
            {["failed", "cancelled", "stale"].includes(review.status) && (
              <button type="button" disabled={busy} onClick={() => void retryReview()}>
                重试 AI 审阅
              </button>
            )}
            {review.findings.length === 0 && review.status === "completed" && (
              <p>模型未提出具体问题；这不证明不存在问题。</p>
            )}
            <ol>
              {review.findings.map((finding) => (
                <li key={finding.findingId}>
                  <p>
                    <strong>
                      {finding.severity} · {finding.category}
                    </strong>
                    ：{finding.description}
                  </p>
                  <p>
                    位置：<code>{finding.pointer || "/"}</code>
                  </p>
                  <ReviewSource
                    pointer={finding.pointer}
                    root={{
                      definition: guide.definition,
                      canary: { binding: guide.canary, trace: files.trace, report: files.report },
                    }}
                  />
                  <p>依据：{finding.reasoning}</p>
                  <p>建议：{finding.recommendation}</p>
                  {review.stage === "canary" && review.status === "completed" && (
                    <>
                      <label>
                        处理意见
                        <select
                          aria-label={`审阅意见 ${finding.findingId} 的处理`}
                          value={decisions[finding.findingId]?.disposition ?? ""}
                          onChange={(event) => {
                            const value = event.target.value as "" | "acknowledged" | "disagree";
                            setDecisions((current) => ({
                              ...current,
                              [finding.findingId]: {
                                disposition: value,
                                reason: current[finding.findingId]?.reason ?? "",
                              },
                            }));
                          }}
                        >
                          <option value="">请选择</option>
                          <option value="acknowledged">已审阅</option>
                          <option value="disagree">不同意</option>
                        </select>
                      </label>
                      {decisions[finding.findingId]?.disposition === "disagree" && (
                        <label>
                          不同意的理由
                          <textarea
                            aria-label={`审阅意见 ${finding.findingId} 的不同意理由`}
                            maxLength={2000}
                            value={decisions[finding.findingId]?.reason ?? ""}
                            onChange={(event) =>
                              setDecisions((current) => ({
                                ...current,
                                [finding.findingId]: {
                                  disposition: "disagree",
                                  reason: event.target.value,
                                },
                              }))
                            }
                          />
                        </label>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
        {!guide.canaryCurrent && (
          <p role="status">当前验收不存在、未通过或绑定已失效，不能批准。</p>
        )}
        {(error || bound.problem) && <p role="alert">{error || bound.problem}</p>}
        {evidence && (
          <>
            <p role="status">
              已自动核对当前登记的验收证据。验证结果：{evidence.report.passed ? "通过" : "未通过"}。
            </p>
            <dl className="guide-review-identities">
              <dt>内容摘要</dt>
              <dd>
                <code>{guide.definition.contentDigest}</code>
              </dd>
              <dt>运行证据摘要</dt>
              <dd>
                <code>{evidence.report.traceDigest}</code>
              </dd>
            </dl>
            <h3>目标与实际计划</h3>
            {evidence.trace.runnerId && (
              <p>
                验收运行器：<code>{evidence.trace.runnerId}</code>
              </p>
            )}
            <p>{guide.definition.canary.goal}</p>
            <ol>
              {evidence.trace.plan.steps.map((step) => (
                <li key={step.stepId}>
                  {step.title}（{step.status}）
                  {step.acceptanceCriteria ? `：${step.acceptanceCriteria}` : ""}
                </li>
              ))}
            </ol>
            <h3>实际工具调用与输出</h3>
            {evidence.trace.actions.map((action) => (
              <details key={action.request.actionId}>
                <summary>
                  {action.tool}.{action.function} · {action.observation.status}
                </summary>
                <p>
                  Observation：<code>{action.observation.observationId}</code>；Grant：
                  <code>{action.grant.grantId}</code>
                </p>
                <p>
                  服务：<code>{action.request.serverId}</code>；函数合同摘要：
                  <code>{action.request.functionSchemaDigest}</code>
                </p>
                <h4>关键参数</h4>
                <pre className="guide-review-output">
                  {JSON.stringify(action.request.arguments, null, 2)}
                </pre>
                <h4>计算输出</h4>
                <pre className="guide-review-output">
                  {JSON.stringify(action.observation.result, null, 2)}
                </pre>
              </details>
            ))}
            <h3>断言、失败与预算</h3>
            <ul>
              {evidence.report.assertions.map((assertion) => (
                <li key={assertion.assertionId}>
                  {assertion.assertionId}：{assertion.passed ? "通过" : "未通过"}
                </li>
              ))}
            </ul>
            <pre className="guide-review-output">
              {JSON.stringify(
                {
                  failures: evidence.trace.failures,
                  actionRejections: evidence.trace.actionRejections,
                  problems: evidence.report.problems,
                  actual: evidence.trace.observedBudget,
                  maximum: guide.definition.canary.budget,
                  cleanup: evidence.trace.cleanup,
                },
                null,
                2,
              )}
            </pre>
            <h3>最终结论</h3>
            <pre className="guide-review-output">
              {JSON.stringify(evidence.trace.final.response, null, 2)}
            </pre>
            <h3>披露与适用局限</h3>
            <p>
              本次验收使用固定非私密输入；模型与工具输出属于计算证据，不是实验验证。授权摘要：
              <code>{evidence.trace.authorizationEnvelopeDigest}</code>。
            </p>
            <ul>
              {guide.definition.guidance.limitations.map((limitation, index) => (
                <li key={index}>{limitation}</li>
              ))}
            </ul>
          </>
        )}
        <fieldset disabled={busy || !canApprove}>
          <legend>管理员明确审阅确认</legend>
          {confirmations.map((label, index) => (
            <label key={label} className="guide-review-confirmation">
              <input
                type="checkbox"
                checked={checks[index]}
                onChange={(event) =>
                  setChecks((current) =>
                    current.map((value, i) => (i === index ? event.target.checked : value)),
                  )
                }
              />
              {label}
            </label>
          ))}
        </fieldset>
        <div className="modal-actions">
          <button type="button" disabled={busy} onClick={onClose}>
            返回列表
          </button>
          <button
            type="button"
            disabled={busy || !canApprove || !checks.every(Boolean)}
            onClick={() => void submit()}
          >
            {busy ? "正在提交批准…" : "批准当前内容与验收证据"}
          </button>
        </div>
      </section>
    </div>
  );
}

function ReviewSource({ pointer, root }: { pointer: string; root: unknown }) {
  let value = root;
  if (pointer !== "") {
    if (!pointer.startsWith("/")) return null;
    for (const part of pointer.slice(1).split("/")) {
      const key = part.replace(/~1/g, "/").replace(/~0/g, "~");
      if (value === null || typeof value !== "object" || !Object.hasOwn(value, key)) return null;
      value = (value as Record<string, unknown>)[key];
    }
  }
  if (value === undefined) return null;
  return (
    <details>
      <summary>查看对应原文</summary>
      <pre className="guide-review-output">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function reviewStatusLabel(status: GuideReviewSummary["status"]): string {
  return {
    queued: "排队中",
    running: "审阅中",
    cleaning: "清理会话中",
    completed: "已完成",
    failed: "失败",
    cancelled: "已取消",
    stale: "已失效",
  }[status];
}
